import { Component, signal } from '@angular/core';
import { AngularModule } from '../../../../modules/angular.module';
import { LanguageCode, Lesson } from '@mira-avant/mtlibs';
import { LessonService } from '../../../../services/lesson.service';
import { NavigationService } from '../../../../services/navigation.service';
import { AdminService } from '../../../../services/admin.service';
import { ZorroModule } from '../../../../modules/zorro.module';
import { LanguageSelectorComponent } from '../../language-selector/language-selector.component';
import { LessonFormComponent } from '../../../forms/standard/lesson-form/lesson-form.component';
import { EMPTY_LESSON, LessonText, _ } from '@mira-avant/mtlibs';
import { v4 } from 'uuid';
import { ClipboardService } from '../../../../services/clipboard.service';
import { Subscription } from 'rxjs';
import { InProgressService } from '../../../../services/in-progress.service';
import { TableFormContainer } from '../table-form-container/table-form-container';
import {
	CmsTable,
	Column,
	ENABLE_COPY_COLUMN,
	ENABLE_DELETE_COLUMN,
	ENABLE_EDIT_COLUMN,
	ENABLE_PREVIEW_COLUMN,
	ENABLE_DISPLAY_ORDER_COLUMN,
	ENABLE_UP_DOWN_COLUMN,
	ENABLE_CHECKBOX_COLUMN,
} from '../cms-table/cms-table';
import { RowColumn } from '../cms-table/row-column';
import { HeaderColumn } from '../cms-table/header-column';
import { AuthService } from '../../../../services/auth.service';

@Component({
	selector: 'app-lesson-list',
	standalone: true,
	imports: [
		AngularModule,
		ZorroModule,
		LanguageSelectorComponent,
		LessonFormComponent,
		TableFormContainer,
		CmsTable,
		RowColumn,
		HeaderColumn,
	],
	templateUrl: './lesson-list.component.html',
	styleUrl: './lesson-list.component.scss',
})
export class LessonListComponent {
	readonly loading = signal<boolean>(true);
	readonly lessons = signal<Lesson[]>([]);
	readonly uuidsInProgress = signal<string[]>([]);
	readonly editLanguage = signal<string>('en');
	readonly editLesson = signal<Lesson | undefined>(undefined);
	readonly columns = signal<Column[]>([]);
	readonly isAdmin = signal<boolean>(false);
	readonly isFormShowing = signal<boolean>(false);

	private sub: Subscription | undefined;

	constructor(
		public lessonService: LessonService,
		private authService: AuthService,
		public nav: NavigationService,
		private adminService: AdminService,
		public clipboardService: ClipboardService,
		private inProgressService: InProgressService
	) {
		this.sub = this.lessonService.lessons$.subscribe((lessons: Lesson[]) => {
			this.applyLessons(lessons);

			this.loading.set(false);
		});

		this.authService.isAdmin$().then((isAdmin: boolean) => {
			this.isAdmin.set(isAdmin);
			this.updateTableDetails();
		});
	}

	private updateTableDetails(): void {
		const columns: Column[] = [];

		if (this.isAdmin()) {
			columns.push(ENABLE_CHECKBOX_COLUMN);
		}

		columns.push(ENABLE_DISPLAY_ORDER_COLUMN, {
			key: 'lessonName',
			name: 'Name',
			sortOrder: null,
			sortFn: (a: Lesson, b: Lesson) => a.active_text?.name.localeCompare(b.active_text?.name || '') || 0,
			sortDirections: ['ascend', 'descend', null],
			filterMultiple: true,
			filterOptions: this.lessons().map(row => ({
				text: row.active_text?.name || '',
				value: row.active_text?.name || '',
			})),
			filterFn: (list: string[], item: Lesson) => list.some(name => item.active_text?.name.indexOf(name) !== -1),
		});

		if (this.isAdmin()) {
			columns.push(ENABLE_COPY_COLUMN);
			columns.push(ENABLE_UP_DOWN_COLUMN);
			columns.push(ENABLE_EDIT_COLUMN);
			columns.push(ENABLE_DELETE_COLUMN);
		}

		this.columns.set(columns);
	}

	public select($event: unknown): void {
		const lesson = $event as Lesson;

		if (!this.adminService.selectedClient$.value) {
			throw new Error('No client selected');
		}

		if (!this.adminService.selectedCourse$.value) {
			throw new Error('No course selected');
		}

		if (!this.adminService.selectedUnit$.value) {
			throw new Error('No unit selected');
		}

		this.nav.navigate([
			'my',
			'admin',
			'lc',
			{
				clientUuid: this.adminService.selectedClient$.value.uuid,
				courseUuid: this.adminService.selectedCourse$.value.uuid,
				unitUuid: this.adminService.selectedUnit$.value.uuid,
				lessonUuid: lesson.uuid,
			},
		]);
	}

	public copy($event: object): void {
		const lesson = $event as Lesson;
		this.clipboardService.copyLesson(lesson);
	}

	public copyBulk($event: object[]): void {
		const lessons = $event as Lesson[];
		this.clipboardService.copyLessons(lessons);
	}

	public edit($event: unknown): void {
		const lesson = $event as Lesson;
		this.editLesson.set(this.editLesson()?.uuid === lesson.uuid ? undefined : lesson);
		this.isFormShowing.set(this.editLesson() !== undefined);
	}

	public onShowForm(): void {
		this.isFormShowing.set(true);
	}

	public onHideForm(): void {
		this.isFormShowing.set(false);
		this.resetEditState();
	}

	private resetEditState(): void {
		this.editLesson.set(undefined);
		this.editLanguage.set(LanguageCode.EN);
	}

	public async delete$($event: unknown): Promise<void> {
		const lesson = $event as Lesson;
		this.loading.set(true);
		try {
			await this.lessonService.delete$(lesson.uuid);
			this.lessons.set(this.lessons().filter(l => l.uuid !== lesson.uuid));
		} catch (error) {
			console.error('Error deleting lesson:', error);
		} finally {
			this.loading.set(false);
		}
	}

	public async moveUp$($event: unknown): Promise<void> {
		const lesson = $event as Lesson;
		if (!this.adminService.selectedUnit$.value) {
			throw new Error('No unit selected');
		}
		this.loading.set(true);
		try {
			const updatedLessons = await this.lessonService.moveUp$(
				lesson.uuid,
				this.adminService.selectedUnit$.value.uuid
			);
			this.lessons.set(updatedLessons);
		} catch (error) {
			console.error('Error moving lesson up:', error);
		} finally {
			this.loading.set(false);
		}
	}

	public async moveDown$($event: unknown): Promise<void> {
		const lesson = $event as Lesson;
		if (!this.adminService.selectedUnit$.value) {
			throw new Error('No unit selected');
		}
		this.loading.set(true);
		try {
			const updatedLessons = await this.lessonService.moveDown$(
				lesson.uuid,
				this.adminService.selectedUnit$.value.uuid
			);
			this.lessons.set(updatedLessons);
		} catch (error) {
			console.error('Error moving lesson down:', error);
		} finally {
			this.loading.set(false);
		}
	}

	public async onLessonFormSubmit$($event: any): Promise<void> {
		let submitSucceeded = false;
		this.loading.set(true);
		try {
			if (this.editLesson()) {
				await this.updateLesson$($event);
			} else {
				await this.createNewLesson$($event);
			}

			submitSucceeded = true;
		} catch (error) {
			console.error('Error submitting lesson form:', error);
		} finally {
			this.loading.set(false);
			this.resetEditState();

			if (submitSucceeded) {
				this.isFormShowing.set(false);
			}
		}
	}

	private applyLessons(lessons: Lesson[]): void {
		this.lessons.set(lessons);
		this.updateTableDetails();

		const lessonUuids: string[] = lessons.map(lesson => lesson.uuid);

		this.inProgressService.lessonsInProgress$(lessonUuids).then((uuids: string[]) => {
			this.uuidsInProgress.set(uuids);
		});

		const currentEditLesson = this.editLesson();
		if (currentEditLesson) {
			const refreshedEditLesson = lessons.find(lesson => lesson.uuid === currentEditLesson.uuid);
			this.editLesson.set(refreshedEditLesson);
		}
	}

	private async createNewLesson$($event: any): Promise<void> {
		if (!this.adminService.selectedUnit$.value) {
			throw new Error('No unit selected');
		}

		await this.lessonService.create$({
			unit_uuid: this.adminService.selectedUnit$.value.uuid,
			default_lang_id: 'en',
			copied_from_uuid: null,
			lesson_text: [
				{
					default_lang_id: 'en',
					name: $event.name,
					image_uri: $event.imageUri,
					description_short: $event.descriptionShort,
					description_html: $event.descriptionHtml,
					created: new Date(),
					updated: new Date(),
				},
			],
		});
	}

	private async updateLesson$($event: any): Promise<void> {
		const currentEditLesson = this.editLesson();
		if (!currentEditLesson) {
			throw new Error('No lesson to update');
		}

		const lessonTexts: LessonText[] = currentEditLesson.lesson_text;

		let lessonText: LessonText | undefined = lessonTexts.find(text => text.default_lang_id === this.editLanguage());

		if (!lessonText) {
			lessonText = {
				uuid: v4(),
				lesson_uuid: currentEditLesson.uuid,
				default_lang_id: this.editLanguage(),
				name: $event.name,
				image_uri: $event.imageUri,
				description_short: $event.descriptionShort,
				description_html: $event.descriptionHtml,
				created: new Date(),
				updated: new Date(),
			};

			lessonTexts.push(lessonText);
		} else {
			lessonText.name = $event.name;
			lessonText.image_uri = $event.imageUri;
			lessonText.description_short = $event.descriptionShort;
			lessonText.description_html = $event.descriptionHtml;
			lessonText.updated = new Date();
		}

		const props = Object.keys(EMPTY_LESSON);
		const payload: Lesson = _.pick(currentEditLesson, props) as Lesson;

		await this.lessonService.update$(currentEditLesson.uuid, {
			...payload,
			lesson_text: lessonTexts,
		});
	}

	public onEditLanguageChange(code: string): void {
		this.editLanguage.set(code);
	}

	ngOnDestroy(): void {
		this.sub?.unsubscribe();
	}
}
